<script>
	import { onMount, onDestroy } from 'svelte';
	import { teamAPI } from './api.js';
	import Icon from './lib/Icon.svelte';

	let stats = $state([]);
	let teams = $state([]);
	let loading = $state(true);
	let error = $state('');
	let rangeDays = $state(14);
	let activeTab = $state('roster'); // roster | teams

	// Team modal
	let showTeam = $state(false);
	let editingTeam = $state(null);
	let teamForm = $state({ name: '', description: '', color: '#4f6df5', auto_assign_strategy: 'least_busy' });

	const PRESENCE_DOT = {
		online: 'var(--success)',
		busy: 'var(--danger)',
		away: 'var(--warning)',
		offline: 'var(--text-3)'
	};
	const PRESENCE_LABEL = {
		online: 'En línea', busy: 'Ocupado', away: 'Ausente', offline: 'Desconectado'
	};
	const STRATEGY_LABEL = {
		least_busy: 'Menos ocupado',
		round_robin: 'Round robin',
		manual: 'Manual'
	};

	let totalMessages = $derived(stats.reduce((s, m) => s + (m.stats?.messages_sent || 0), 0));
	let totalResolved = $derived(stats.reduce((s, m) => s + (m.stats?.conversations_resolved || 0), 0));
	let onlineCount = $derived(stats.filter(m => m.presence_status === 'online').length);

	async function loadAll() {
		loading = true;
		error = '';
		try {
			const [s, t] = await Promise.all([
				teamAPI.getStats(rangeDays),
				teamAPI.listTeams()
			]);
			stats = s || [];
			teams = t || [];
		} catch (e) {
			error = e.message || 'Error cargando equipo';
		} finally {
			loading = false;
		}
	}

	function openCreateTeam() {
		editingTeam = null;
		teamForm = { name: '', description: '', color: '#4f6df5', auto_assign_strategy: 'least_busy' };
		showTeam = true;
	}
	function openEditTeam(t) {
		editingTeam = t;
		teamForm = {
			name: t.name,
			description: t.description || '',
			color: t.color || '#4f6df5',
			auto_assign_strategy: t.auto_assign_strategy || 'least_busy'
		};
		showTeam = true;
	}
	async function saveTeam(e) {
		e.preventDefault();
		try {
			if (editingTeam) {
				await teamAPI.updateTeam(editingTeam.id, teamForm);
			} else {
				await teamAPI.createTeam(teamForm);
			}
			showTeam = false;
			await loadAll();
		} catch (err) {
			error = err.message || 'No se pudo guardar el equipo';
		}
	}
	async function deleteTeam(t) {
		if (!confirm(`¿Eliminar el equipo "${t.name}"? Sus miembros quedarán sin equipo.`)) return;
		try {
			await teamAPI.deleteTeam(t.id);
			await loadAll();
		} catch (err) {
			error = err.message || 'No se pudo eliminar';
		}
	}

	async function setMyPresence(status) {
		try {
			await teamAPI.setMyPresence(status);
			await loadAll();
		} catch (err) {
			error = err.message || 'No se pudo cambiar la presencia';
		}
	}

	async function changeUserTeam(user, teamId) {
		try {
			if (teamId) {
				await teamAPI.addMember(teamId, user.id);
			} else if (user.team_id) {
				await teamAPI.removeMember(user.team_id, user.id);
			}
			await loadAll();
		} catch (err) {
			error = err.message || 'No se pudo cambiar el equipo';
		}
	}

	onMount(loadAll);

	function fmtTeamName(id) {
		if (!id) return 'Sin equipo';
		return teams.find(t => t.id === id)?.name || 'Sin equipo';
	}

	function loadPct(m) {
		const open = m.stats?.open_now ?? m.open_conversations ?? 0;
		const cap = Math.max(1, m.max_concurrent_conversations || 10);
		return Math.min(100, Math.round((open / cap) * 100));
	}
</script>

<div class="team-view">
	<!-- Toolbar -->
	<div class="toolbar">
		<div class="toolbar-left">
			<div class="tabs">
				<button class:active={activeTab === 'roster'} onclick={() => (activeTab = 'roster')}>
					<Icon name="users" size={14} /> Equipo
				</button>
				<button class:active={activeTab === 'teams'} onclick={() => (activeTab = 'teams')}>
					<Icon name="building" size={14} /> Departamentos
				</button>
			</div>
			{#if activeTab === 'roster'}
				<div class="kpis">
					<div class="kpi-pill"><span class="dot" style="background:{PRESENCE_DOT.online}"></span> {onlineCount} en línea</div>
					<div class="kpi-pill brand"><Icon name="message-square" size={12} /> {totalMessages} mensajes ({rangeDays}d)</div>
					<div class="kpi-pill success"><Icon name="check" size={12} /> {totalResolved} resueltas</div>
				</div>
			{/if}
		</div>
		<div class="toolbar-right">
			{#if activeTab === 'roster'}
				<div class="range">
					{#each [7, 14, 30] as d}
						<button class:active={rangeDays === d} onclick={() => { rangeDays = d; loadAll(); }}>{d}d</button>
					{/each}
				</div>
				<div class="presence-quick">
					<button class="quick" onclick={() => setMyPresence('online')} title="Marcar como en línea">
						<span class="dot" style="background:{PRESENCE_DOT.online}"></span> Online
					</button>
					<button class="quick" onclick={() => setMyPresence('away')} title="Marcar como ausente">
						<span class="dot" style="background:{PRESENCE_DOT.away}"></span> Away
					</button>
				</div>
			{:else}
				<button class="btn btn-primary btn-sm" onclick={openCreateTeam}>
					<Icon name="plus" size={14} /> Nuevo equipo
				</button>
			{/if}
		</div>
	</div>

	{#if error}
		<div class="banner-error">
			<Icon name="alert-circle" size={14} /> {error}
			<button class="banner-close" onclick={() => (error = '')}><Icon name="x" size={14} /></button>
		</div>
	{/if}

	<div class="body">
		{#if loading}
			<div class="loading"><div class="spinner"></div></div>
		{:else if activeTab === 'roster'}
			{#if !stats.length}
				<div class="empty card card-padded">
					<Icon name="users" size={32} />
					<h3>Aún no hay agentes humanos</h3>
					<p class="muted">Invita a agentes desde la sección "Usuarios" para verlos aquí.</p>
				</div>
			{:else}
				<div class="grid">
					{#each stats as m (m.id)}
						{@const pct = loadPct(m)}
						<article class="agent-card card">
							<div class="card-row">
								<div class="ident">
									<div class="ag-avatar">
										<span class="presence-dot" style="background:{PRESENCE_DOT[m.presence_status] || PRESENCE_DOT.offline}"></span>
										<span class="initials">{(m.name || '?').charAt(0)}</span>
									</div>
									<div class="ident-text">
										<h3>{m.name}</h3>
										<div class="sub">
											<span class="role-pill">{m.role}</span>
											<span class="muted-sm">{m.title || PRESENCE_LABEL[m.presence_status] || ''}</span>
										</div>
									</div>
								</div>
							</div>

							{#if m.presence_message}
								<div class="status-msg">"{m.presence_message}"</div>
							{/if}

							<div class="kpi-row">
								<div class="kpi"><div class="kpi-num">{m.stats?.messages_sent ?? 0}</div><div class="kpi-lbl">Mensajes</div></div>
								<div class="kpi"><div class="kpi-num">{m.stats?.conversations_owned ?? 0}</div><div class="kpi-lbl">Asignadas</div></div>
								<div class="kpi"><div class="kpi-num">{m.stats?.conversations_resolved ?? 0}</div><div class="kpi-lbl">Resueltas</div></div>
							</div>

							<div class="capacity">
								<div class="cap-row">
									<span>Carga actual</span>
									<span class="cap-val">{m.stats?.open_now ?? 0} / {m.max_concurrent_conversations || 10}</span>
								</div>
								<div class="cap-bar">
									<div
										class="cap-fill"
										class:warn={pct >= 70 && pct < 100}
										class:full={pct >= 100}
										style="width:{pct}%"
									></div>
								</div>
							</div>

							<div class="card-foot">
								<div class="team-select-wrap">
									<Icon name="building" size={12} class="team-icon" />
									<select
										value={m.team_id ?? ''}
										onchange={(e) => changeUserTeam(m, e.target.value ? parseInt(e.target.value) : null)}
									>
										<option value="">Sin equipo</option>
										{#each teams as t}
											<option value={t.id}>{t.name}</option>
										{/each}
									</select>
								</div>
								<span class="handoff-flag" class:on={m.available_for_handoff}>
									<Icon name="sparkles" size={11} />
									{m.available_for_handoff ? 'Acepta handoff' : 'No handoff'}
								</span>
							</div>
						</article>
					{/each}
				</div>
			{/if}
		{:else if activeTab === 'teams'}
			{#if !teams.length}
				<div class="empty card card-padded">
					<Icon name="building" size={32} />
					<h3>Sin equipos / departamentos</h3>
					<p class="muted">Agrupa a tus agentes por departamento (Ventas, Soporte, etc.) y define cómo se enrutan las conversaciones.</p>
					<button class="btn btn-primary" onclick={openCreateTeam}>
						<Icon name="plus" size={14} /> Crear equipo
					</button>
				</div>
			{:else}
				<div class="grid">
					{#each teams as t (t.id)}
						<article class="team-card card">
							<header class="team-head">
								<div class="team-color" style="background:{t.color}"></div>
								<div class="team-text">
									<h3>{t.name}</h3>
									{#if t.description}<p class="muted">{t.description}</p>{/if}
								</div>
								<div class="team-actions">
									<button class="icon-btn" onclick={() => openEditTeam(t)} title="Editar">
										<Icon name="edit" size={14} />
									</button>
									<button class="icon-btn danger" onclick={() => deleteTeam(t)} title="Eliminar">
										<Icon name="trash" size={14} />
									</button>
								</div>
							</header>
							<div class="team-meta">
								<span class="meta-pill"><Icon name="users" size={11} /> {t.member_count} {t.member_count === 1 ? 'miembro' : 'miembros'}</span>
								<span class="meta-pill"><Icon name="zap" size={11} /> {STRATEGY_LABEL[t.auto_assign_strategy] || t.auto_assign_strategy}</span>
								{#if t.is_default}<span class="badge badge-accent">Default</span>{/if}
							</div>
							{#if t.members?.length}
								<div class="member-avatars">
									{#each t.members.slice(0, 6) as mb}
										<div class="mini-avatar" title={mb.name}>{mb.name?.charAt(0)}</div>
									{/each}
									{#if t.members.length > 6}
										<div class="mini-avatar more">+{t.members.length - 6}</div>
									{/if}
								</div>
							{/if}
						</article>
					{/each}
				</div>
			{/if}
		{/if}
	</div>
</div>

<!-- ===== Team modal ===== -->
{#if showTeam}
	<div class="modal-back" onclick={() => (showTeam = false)} role="presentation">
		<div class="modal" onclick={(e) => e.stopPropagation()} role="dialog">
			<div class="modal-head">
				<h3>{editingTeam ? 'Editar equipo' : 'Nuevo equipo'}</h3>
				<button class="icon-btn" onclick={() => (showTeam = false)} aria-label="Cerrar">
					<Icon name="x" size={16} />
				</button>
			</div>
			<form onsubmit={saveTeam}>
				<label>Nombre *
					<input type="text" bind:value={teamForm.name} required placeholder="Ventas, Soporte, etc." />
				</label>
				<label>Descripción
					<input type="text" bind:value={teamForm.description} placeholder="Atiende preguntas comerciales..." />
				</label>
				<div class="form-row">
					<label>Color
						<input type="color" bind:value={teamForm.color} />
					</label>
					<label>Estrategia de asignación
						<select bind:value={teamForm.auto_assign_strategy}>
							<option value="least_busy">Menos ocupado</option>
							<option value="round_robin">Round robin</option>
							<option value="manual">Manual</option>
						</select>
					</label>
				</div>
				<div class="modal-foot">
					<button type="button" class="btn btn-secondary" onclick={() => (showTeam = false)}>Cancelar</button>
					<button type="submit" class="btn btn-primary">{editingTeam ? 'Guardar' : 'Crear'}</button>
				</div>
			</form>
		</div>
	</div>
{/if}

<style>
	.team-view {
		height: 100%;
		display: flex; flex-direction: column;
		background: var(--surface-1);
	}
	.toolbar {
		display: flex; align-items: center; justify-content: space-between;
		gap: 12px; padding: 14px 24px;
		background: var(--surface-0);
		border-bottom: 1px solid var(--border-1);
		flex-shrink: 0;
	}
	.toolbar-left { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
	.toolbar-right { display: flex; align-items: center; gap: 10px; }

	.tabs {
		display: flex; background: var(--surface-2);
		border-radius: 8px; padding: 3px;
	}
	.tabs button {
		display: flex; align-items: center; gap: 6px;
		padding: 6px 12px;
		font-size: 12px; font-weight: 600;
		color: var(--text-3);
		border-radius: 5px;
		transition: all var(--transition-fast);
	}
	.tabs button.active { background: var(--surface-0); color: var(--text-1); box-shadow: var(--shadow-xs); }

	.kpis { display: flex; gap: 8px; flex-wrap: wrap; }
	.kpi-pill {
		display: inline-flex; align-items: center; gap: 6px;
		padding: 5px 10px;
		background: var(--surface-2);
		border-radius: 20px;
		font-size: 12px; font-weight: 600;
		color: var(--text-2);
	}
	.kpi-pill.brand { background: var(--brand-soft); color: var(--brand-dark); }
	.kpi-pill.success { background: var(--success-soft); color: var(--success); }

	.range {
		display: flex; gap: 2px;
		background: var(--surface-2); padding: 3px;
		border-radius: 6px;
	}
	.range button {
		padding: 4px 10px; font-size: 11.5px;
		font-weight: 600; color: var(--text-3);
		border-radius: 4px;
	}
	.range button.active { background: var(--surface-0); color: var(--text-1); box-shadow: var(--shadow-xs); }

	.presence-quick { display: flex; gap: 4px; }
	.quick {
		display: flex; align-items: center; gap: 6px;
		padding: 5px 10px;
		font-size: 12px; font-weight: 500;
		color: var(--text-2);
		background: var(--surface-1);
		border-radius: 6px;
		transition: background var(--transition-fast);
	}
	.quick:hover { background: var(--surface-2); }

	.banner-error {
		display: flex; align-items: center; gap: 8px;
		background: var(--danger-soft); color: var(--danger);
		padding: 8px 16px; font-size: 13px;
		border-bottom: 1px solid var(--border-1);
	}
	.banner-close {
		margin-left: auto; width: 22px; height: 22px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 4px; color: var(--danger);
	}
	.banner-close:hover { background: rgba(239, 68, 68, 0.15); }

	.body { flex: 1; overflow-y: auto; padding: 18px 24px 24px; }
	.loading { display: flex; align-items: center; justify-content: center; padding: 60px; }
	.spinner {
		width: 32px; height: 32px;
		border: 3px solid var(--border-1);
		border-top-color: var(--accent);
		border-radius: 50%;
		animation: spin 700ms linear infinite;
	}
	@keyframes spin { to { transform: rotate(360deg); } }

	.empty {
		max-width: 520px; margin: 60px auto 0; text-align: center;
		display: flex; flex-direction: column; align-items: center; gap: 10px;
		color: var(--text-3);
	}
	.empty h3 { color: var(--text-1); margin-top: 4px; }
	.empty p { font-size: 14px; line-height: 1.5; }
	.muted { color: var(--text-3); }
	.muted-sm { color: var(--text-3); font-size: 12px; }

	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(310px, 1fr));
		gap: 14px;
	}

	/* Agent card */
	.agent-card {
		padding: 16px;
		display: flex; flex-direction: column; gap: 12px;
		transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
	}
	.agent-card:hover {
		border-color: var(--border-2);
		box-shadow: var(--shadow-sm);
	}
	.card-row { display: flex; align-items: center; justify-content: space-between; }
	.ident { display: flex; gap: 12px; align-items: center; min-width: 0; }
	.ag-avatar {
		position: relative;
		width: 44px; height: 44px;
		border-radius: 50%;
		background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%);
		color: #fff;
		display: flex; align-items: center; justify-content: center;
		font-size: 16px; font-weight: 600;
		flex-shrink: 0;
	}
	.presence-dot {
		position: absolute;
		bottom: -2px; right: -2px;
		width: 12px; height: 12px;
		border-radius: 50%;
		border: 2px solid var(--surface-0);
	}
	.initials { line-height: 1; }
	.ident-text h3 { font-size: 14px; margin-bottom: 4px; }
	.sub { display: flex; gap: 6px; align-items: center; }
	.role-pill {
		font-size: 10.5px; padding: 2px 7px;
		background: var(--accent-soft); color: var(--accent);
		border-radius: 4px; font-weight: 600;
		text-transform: capitalize;
	}

	.status-msg {
		font-size: 12px;
		color: var(--text-2);
		font-style: italic;
		padding: 6px 10px;
		background: var(--surface-1);
		border-radius: 6px;
		border-left: 2px solid var(--accent);
	}

	.kpi-row {
		display: grid; grid-template-columns: repeat(3, 1fr);
		gap: 6px;
		padding: 10px 0;
		border-top: 1px solid var(--border-1);
		border-bottom: 1px solid var(--border-1);
	}
	.kpi { text-align: center; }
	.kpi-num { font-size: 16px; font-weight: 700; color: var(--text-1); }
	.kpi-lbl { font-size: 10.5px; color: var(--text-3); }

	.capacity { display: flex; flex-direction: column; gap: 4px; }
	.cap-row {
		display: flex; justify-content: space-between;
		font-size: 11.5px;
		color: var(--text-3);
	}
	.cap-val { font-weight: 600; color: var(--text-1); }
	.cap-bar {
		height: 6px; background: var(--surface-2);
		border-radius: 3px; overflow: hidden;
	}
	.cap-fill {
		height: 100%; background: var(--success);
		transition: width 400ms cubic-bezier(0.4, 0, 0.2, 1);
	}
	.cap-fill.warn { background: var(--warning); }
	.cap-fill.full { background: var(--danger); }

	.card-foot {
		display: flex; align-items: center; justify-content: space-between;
		gap: 10px;
	}
	.team-select-wrap {
		display: flex; align-items: center; gap: 6px;
		flex: 1; min-width: 0;
	}
	.team-select-wrap :global(.team-icon) { color: var(--text-3); flex-shrink: 0; }
	.team-select-wrap select {
		font-size: 12px !important;
		padding: 4px 24px 4px 6px !important;
		height: auto !important;
	}
	.handoff-flag {
		display: inline-flex; align-items: center; gap: 4px;
		padding: 3px 8px;
		font-size: 10.5px;
		font-weight: 600;
		background: var(--surface-2);
		color: var(--text-3);
		border-radius: 4px;
	}
	.handoff-flag.on { background: var(--success-soft); color: var(--success); }

	/* Team card */
	.team-card { padding: 16px; }
	.team-head {
		display: flex; gap: 10px; align-items: flex-start;
		margin-bottom: 12px;
	}
	.team-color {
		width: 6px; align-self: stretch;
		border-radius: 3px;
		min-height: 32px;
	}
	.team-text { flex: 1; min-width: 0; }
	.team-text h3 { font-size: 15px; margin-bottom: 2px; }
	.team-text p { font-size: 12.5px; line-height: 1.4; }
	.team-actions { display: flex; gap: 4px; }
	.icon-btn {
		width: 28px; height: 28px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 6px;
		color: var(--text-3);
	}
	.icon-btn:hover { background: var(--surface-2); color: var(--text-1); }
	.icon-btn.danger:hover { background: var(--danger-soft); color: var(--danger); }

	.team-meta {
		display: flex; gap: 6px; flex-wrap: wrap;
		margin-bottom: 12px;
	}
	.meta-pill {
		display: inline-flex; align-items: center; gap: 4px;
		padding: 3px 8px;
		font-size: 11px; font-weight: 600;
		background: var(--surface-2); color: var(--text-2);
		border-radius: 4px;
	}

	.member-avatars { display: flex; gap: 4px; }
	.mini-avatar {
		width: 26px; height: 26px;
		border-radius: 50%;
		background: linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%);
		color: #fff;
		display: flex; align-items: center; justify-content: center;
		font-size: 11px; font-weight: 600;
		border: 2px solid var(--surface-0);
		margin-left: -4px;
	}
	.mini-avatar:first-child { margin-left: 0; }
	.mini-avatar.more { background: var(--surface-2); color: var(--text-2); }

	/* Modal */
	.modal-back {
		position: fixed; inset: 0;
		background: rgba(15, 23, 42, 0.5);
		display: flex; align-items: center; justify-content: center;
		z-index: 100;
	}
	.modal {
		background: var(--surface-0);
		border-radius: 14px;
		box-shadow: var(--shadow-xl);
		width: 100%; max-width: 440px;
		max-height: 90vh; overflow-y: auto;
	}
	.modal-head {
		display: flex; align-items: center; justify-content: space-between;
		padding: 16px 20px;
		border-bottom: 1px solid var(--border-1);
	}
	.modal-head h3 { font-size: 16px; }
	form { padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
	form label { font-size: 12.5px; }
	.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
	.modal-foot {
		display: flex; gap: 8px; justify-content: flex-end;
		padding-top: 8px;
		border-top: 1px solid var(--border-1);
		padding: 16px 0 0;
	}
	.dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; }
</style>
